import React from "react";

const RestaurantList = ({ restaurants, toggleStatus, onSelectRestaurant, onDeleteRestaurant, searchQuery, setSearchQuery, filteredCount }) => {
    return (
        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-2xl border border-white/20">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <span className="text-3xl">🏪</span>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                        Registered Restaurants
                    </h2>
                </div>
                
                {/* Search Bar */}
                <div className="flex-1 max-w-md">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <span className="text-lg text-gray-400">🔍</span>
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name, owner, GST, phone..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                            >
                                <span className="text-lg">✕</span>
                            </button>
                        )}
                    </div>
                    {searchQuery && filteredCount !== undefined && (
                        <div className="mt-2 text-center">
                            <span className="text-sm text-gray-500">
                                Found {filteredCount} restaurant{filteredCount !== 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            
            {restaurants.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <p className="text-gray-500 text-lg">No restaurants registered yet</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {restaurants.map((restaurant, index) => (
                        <div
                            key={restaurant._id}
                            className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 border border-gray-100"
                        >
                            {/* Restaurant Header */}
                            <div className="flex items-center mb-4">
                                <div className="relative">
                                    <img
                                        src={restaurant.logo}
                                        alt="Restaurant Logo"
                                        className="w-16 h-16 rounded-full object-cover shadow-lg border-4 border-white"
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/64/6366f1/ffffff?text=R';
                                        }}
                                    />
                                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white ${
                                        restaurant.active ? 'bg-green-500' : 'bg-red-500'
                                    }`}></div>
                                </div>
                                <div className="ml-4 flex-1">
                                    <h3 className="text-xl font-bold text-gray-800 mb-1">{restaurant.name}</h3>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <span className="flex items-center gap-1">
                                            <span>👤</span>
                                            {restaurant.owner}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Restaurant Info */}
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl">
                                    <div className="text-xs text-gray-500 mb-1">Tax Rate</div>
                                    <div className="text-lg font-bold text-indigo-600">{restaurant?.taxPercentage || "0"}%</div>
                                </div>
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-xl">
                                    <div className="text-xs text-gray-500 mb-1">Status</div>
                                    <div className={`text-lg font-bold ${
                                        restaurant.active ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {restaurant.active ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => toggleStatus(restaurant._id, index)}
                                    className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                                        restaurant.active 
                                            ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl' 
                                            : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl'
                                    }`}
                                >
                                    {restaurant.active ? '⏸️ Deactivate' : '▶️ Activate'}
                                </button>
                                <button
                                    onClick={() => onSelectRestaurant(restaurant)}
                                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-4 py-2 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    📊 Details
                                </button>
                                <button
                                    onClick={() => onDeleteRestaurant(restaurant._id, index)}
                                    className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-red-500 hover:to-red-600 text-white px-4 py-2 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RestaurantList;
